<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <VueCtkDateTimePicker
          id="date_picker_receipts"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          @is-hidden="onIsHiddenPicker"
          @input="onInputDatePicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Date range"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker"
        />
        <app-input
          @input.native="onTransactionSearch"
          v-model="filter_search"
          class="input--300"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-filters
          v-model="filter_property"
          :filters="filter_property_options"
          name="filterType"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__source><div class="table-default__left">Source</div></template>
        <template #h__category><div class="table-default__left">Category</div></template>
        <template #h__details><div class="table-default__left">Details</div></template>
        <template #h__notes><div class="table-default__left">Notes</div></template>
        <template #h__debit><div class="table-default__left">Debit</div></template>
        <template #h__credit><div class="table-default__left">Credit</div></template>
        <template #date="{ row }">{{ dateFormat(row.created_at) }}</template>
        <template #source="{ row }">
          <router-link
            v-if="row.reservation"
            :to="{name: 'hotel-management-reservations-detail-reservation', params: {id: row.reservation.id }}"
            class="table-default__link-border"
          >
            {{ row.reservation.group_name }}
          </router-link>
          <router-link
            v-if="row.house_account"
            :to="{name: 'hotel-management-house-accounts-detail', params: {id: row.house_account.id }}"
            class="table-default__link-border"
          >
            {{ row.house_account.account_name }}
          </router-link>
        </template>
        <template #category="{ row }"><div class="table-default__left">{{ row.category?.name || '-' }}</div></template>
        <template #details="{ row }"><div class="table-default__left">{{ row.details?.name || '-' }}</div></template>
        <template #notes="{ row }"><div class="table-default__left">{{ row.notes || '-' }}</div></template>
        <template #debit="{ row }">
          <div v-if="row.type?.code === 'debit'" class="table-default__text-red table-default__left">{{ amountFormat(row.amount) }} $</div>
        </template>
        <template #credit="{ row }">
          <div class="table-default__left">
            <span v-if="!row.details?.currency" class="table-default__text-green">{{ row.type?.code === 'credit' ? `${amountFormat(row.amount)} $` : '' }}</span>
            <template v-else>
              <span class="table-default__text-green">{{ `${amountFormat(row.amount)} $` }}</span><br>
              {{ `${amountFormat(row.details.amount)} ${row.details.currency}` }}
            </template>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import {
  getHotelManagementTransactions
} from '@/http'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'

export default {
  name: 'HotelManagementTransactions',
  components: { VueCtkDateTimePicker },
  data() {
    return {
      page_loaded: false,
      filter_search: '',
      filter_dates: null,
      custom_shortcuts: [
        { key: 'last7Days', label: 'Last 7 days', value: 7 },
        { key: 'last14Days', label: 'Last 14 days', value: 14 },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'last60Days', label: 'Last 60 days', value: 60 },
      ],
      filter_property: '',
      filter_property_options: [
        { value: '', label: 'All' },
        { value: 'aishi', label: 'Aishi' },
        { value: 'brubru', label: 'Brubru' },
      ],
      table: {
        items: [],
        columns: [
          'date',
          'source',
          'category',
          'details',
          'notes',
          'debit',
          'credit',
        ],
        options: {
          headings: {
            'date': 'Date',
            'source': 'Source',
            'category': 'Category',
            'details': 'Details',
            'notes': 'Notes',
            'debit': 'Debit',
            'credit': 'Credit',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
    }
  },
  async created() {
    await this.fetchItems()
    this.page_loaded = true
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getHotelManagementTransactions({
        page: this.pagination.page,
        page_size: 40,
        search: this.filter_search,
        property: this.filter_property,
        from_date: this.filter_dates?.start,
        to_date: this.filter_dates?.end,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onInputDatePicker() {
      if (!this.$refs.date_picker.pickerOpen) {
        this.pagination.page = 1
        this.fetchItems()
      }
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start) {
        this.pagination.page = 1
        this.fetchItems()
      }
    },
    onChangeFilter() {
      this.filter_search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    amountFormat(amount) {
      return new Intl.NumberFormat('ru-RU').format(amount)
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    onTransactionSearch() {
      this.searchTransaction(this)
    },
    searchTransaction: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
